import { API_URL } from "@/config/constants";
import axios from "axios";
import {
    IPaginatedSkills,
    ISkill,
    ISkillCreate,
    ISkillDuplicate,
    ISkillFile,
    ISkillFileCreate,
    ISkillFileUpdate,
    ISkillListParams,
    ISkillUpdate,
    ISkillVersion,
    ISkillVersionDetail,
} from "./skills.types";

const API = `${API_URL}/admin/skills`;

export const listSkills = async () => {
    const result = await axios.get<IPaginatedSkills>(API, {
        params: { size: 100 },
    });
    return result.data.items;
};

export const listSkillsPaginated = async (params: ISkillListParams) => {
    const result = await axios.get<IPaginatedSkills>(API, {
        params: {
            page: params.page,
            size: params.pageSize ?? 25,
            sort_by: params.sortBy,
            order: params.order,
            organization_id: params.organizationId,
            search: params.search,
        },
    });
    return result.data;
};

export const getSkill = async (id: number) => {
    const result = await axios.get<ISkill>(`${API}/${id}`);
    return result.data;
};

export const getCoreSkillCount = async (
    organizationId?: number | null
): Promise<{ coreCount: number; nameMaxLength: number }> => {
    const params: Record<string, string | number> = {};
    if (organizationId != null) params.organization_id = organizationId;
    const result = await axios.get<{
        core_count: number;
        name_max_length: number;
    }>(`${API}/core-count`, { params });
    return {
        coreCount: result.data.core_count,
        nameMaxLength: result.data.name_max_length,
    };
};

export const checkSkillName = async (
    name: string,
    excludeId?: number
): Promise<{ available: boolean; slug: string }> => {
    const params: Record<string, string> = { name };
    if (excludeId) params.exclude_id = String(excludeId);
    const result = await axios.get<{ available: boolean; slug: string }>(
        `${API}/check-name`,
        { params }
    );
    return result.data;
};

export const createSkill = async (data: ISkillCreate) => {
    const result = await axios.post<ISkill>(API, data);
    return result.data;
};

export const duplicateSkill = async (id: number, data: ISkillDuplicate) => {
    const result = await axios.post<ISkill>(`${API}/${id}/duplicate`, data);
    return result.data;
};

export const updateSkill = async (id: number, data: ISkillUpdate) => {
    const result = await axios.put<ISkill>(`${API}/${id}`, data);
    return result.data;
};

export const deleteSkill = async (id: number) => {
    await axios.delete(`${API}/${id}`);
};

export const publishSkill = async (id: number) => {
    const result = await axios.post<ISkill>(`${API}/${id}/publish`);
    return result.data;
};

export const unpublishSkill = async (id: number) => {
    const result = await axios.post<ISkill>(`${API}/${id}/unpublish`);
    return result.data;
};

export const addSkillFile = async (skillId: number, data: ISkillFileCreate) => {
    const result = await axios.post<ISkillFile>(
        `${API}/${skillId}/files`,
        data
    );
    return result.data;
};

export const updateSkillFile = async (
    skillId: number,
    fileId: number,
    data: ISkillFileUpdate
) => {
    const result = await axios.put<ISkillFile>(
        `${API}/${skillId}/files/${fileId}`,
        data
    );
    return result.data;
};

export const deleteSkillFile = async (skillId: number, fileId: number) => {
    await axios.delete(`${API}/${skillId}/files/${fileId}`);
};

export const snapshotSkill = async (skillId: number) => {
    const result = await axios.post<ISkillVersion | null>(
        `${API}/${skillId}/snapshot`
    );
    return result.data;
};

export const getSkillVersions = async (skillId: number) => {
    const result = await axios.get<ISkillVersion[]>(
        `${API}/${skillId}/versions`
    );
    return result.data;
};

export const getSkillVersion = async (skillId: number, versionId: number) => {
    const result = await axios.get<ISkillVersionDetail>(
        `${API}/${skillId}/versions/${versionId}`
    );
    return result.data;
};
